import type { FlexibleMode } from "../../../../api/work-policy-api.types";
import type {
  WorkPolicyApplicationPolicySnapshot,
  WorkPolicyApplicationScheduleRow
} from "./work-policy-application.types";
import {
  FlexibleScheduleEditor,
  FlexibleScheduleRow
} from "../../../work-type-shared/flexible-schedule-editor/flexible-schedule-editor";
import {
  calculateAutomaticBreakMinutes,
  calculateExpectedEndTime
} from "../../../work-type-management/work-policy.validation";
import { SurfaceIntro } from "./work-policy-application.styles";
import { WorkPolicyScheduleScrollFrame } from "./work-policy-schedule-scroll-frame";

interface FlexibleApplicationFieldsProps {
  mode: FlexibleMode;
  policySnapshot: WorkPolicyApplicationPolicySnapshot | null;
  value: WorkPolicyApplicationScheduleRow[];
  onChange: (value: WorkPolicyApplicationScheduleRow[]) => void;
}

const weekdayLabels = ["월요일", "화요일", "수요일", "목요일", "금요일", "토요일", "일요일"];
const compactWeekdayLabels = ["일", "월", "화", "수", "목", "금", "토"];

function compactDatePresentation(workDate: string): {
  label: string;
  isWeekend: boolean;
} {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(workDate);
  if (!match) return { label: workDate, isWeekend: false };

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return { label: workDate, isWeekend: false };
  }

  const weekday = date.getUTCDay();
  return {
    label: `${month}/${day}(${compactWeekdayLabels[weekday]})`,
    isWeekend: weekday === 0 || weekday === 6
  };
}

function toEditorRow(row: WorkPolicyApplicationScheduleRow): FlexibleScheduleRow {
  const identity = row.workDate ?? `weekday-${row.weekdayNo}`;
  const label = row.workDate ?? weekdayLabels[(row.weekdayNo ?? 1) - 1];
  const datePresentation = row.workDate
    ? compactDatePresentation(row.workDate)
    : null;
  return {
    id: identity,
    label,
    displayLabel: datePresentation?.label,
    isWeekend: datePresentation?.isWeekend,
    startTime: row.startTime?.slice(0, 5) ?? "09:00",
    workMinutes: row.plannedWorkMinutes,
    isDayOff: !row.workday,
    waiveBreakAtFourHours: row.breakWaiver
  };
}

function fromEditorRow(
  source: WorkPolicyApplicationScheduleRow,
  row: FlexibleScheduleRow
): WorkPolicyApplicationScheduleRow {
  return {
    ...source,
    startTime: row.isDayOff ? null : row.startTime,
    plannedWorkMinutes: row.isDayOff ? 0 : row.workMinutes,
    breakWaiver: row.isDayOff ? false : row.waiveBreakAtFourHours,
    workday: !row.isDayOff
  };
}

function targetMinutes(
  mode: FlexibleMode,
  value: WorkPolicyApplicationScheduleRow[],
  snapshot: WorkPolicyApplicationPolicySnapshot | null
) {
  if (!snapshot) return null;
  const baseHours = mode === "DAILY_DATE" ? snapshot.dailyBaseHours : snapshot.weeklyBaseHours;
  if (!Number.isFinite(baseHours) || baseHours <= 0) return null;
  if (mode === "WEEKLY_PATTERN") return Math.round(baseHours * 60);
  const workdayCount = value.filter(row => row.workday).length;
  return workdayCount > 0 ? Math.round(baseHours * 60 * workdayCount) : null;
}

export function FlexibleApplicationFields({
  mode,
  policySnapshot,
  value,
  onChange
}: FlexibleApplicationFieldsProps) {
  const editorRows = value.map(toEditorRow);
  const daily = mode === "DAILY_DATE";
  return (
    <>
      <SurfaceIntro>
        <h4>{daily ? "날짜별 근무 일정을 작성해 주세요" : "주간 반복 근무 패턴을 작성해 주세요"}</h4>
        <p>
          {daily
            ? "신청기간의 실제 날짜마다 일정을 작성하고 필요한 날짜를 한 번에 조정할 수 있습니다."
            : "월요일부터 일요일까지의 7개 행이 신청기간 동안 반복됩니다."}
        </p>
      </SurfaceIntro>
      <WorkPolicyScheduleScrollFrame
        label="탄력 근무 일정"
        title="일정 편집 영역"
      >
        <FlexibleScheduleEditor
          mode={mode}
          presentation="productivity"
          targetMinutes={targetMinutes(mode, value, policySnapshot)}
          sectionGuide={
            daily
              ? "평일·주말·전체 일정을 선택하고 목표시간에 맞게 일괄 조정하세요."
              : "월~일 반복 패턴을 선택하고 주간 목표시간에 맞게 일괄 조정하세요."
          }
          value={editorRows}
          onChange={rows =>
            onChange(rows.map((row, index) => fromEditorRow(value[index], row)))
          }
          getRowPreview={row => {
            if (row.isDayOff) return null;
            const automaticBreakMinutes = calculateAutomaticBreakMinutes(
              row.workMinutes,
              policySnapshot?.breakCalculationEnabled ?? true,
              policySnapshot?.breakWorkMinutes ?? 240,
              policySnapshot?.breakMinutes ?? 30,
              row.waiveBreakAtFourHours
            );
            return {
              automaticBreakMinutes,
              expectedEnd: calculateExpectedEndTime(
                row.startTime,
                row.workMinutes,
                automaticBreakMinutes
              )
            };
          }}
        />
      </WorkPolicyScheduleScrollFrame>
    </>
  );
}
